import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import "./index.less";
import Toast from "../../components/toast";
import LoadingMore from "../../components/loading-more";
import { useInfiniteScroll } from "ahooks";
import { getParamsFromHref, getUnifiedLocation } from "../../utils/common";
import StationItem from "../../components/station-item";
import { CHARGE_CDN, isXPApp } from "../../utils/constants";
import { findNearByStationV2 } from "../../api/stations";

interface StationListResponse {
  list: StationListItem[];
  pageNo: number;
  hasMore: boolean;
}

const sortOptions = [
  { value: 1, label: "综合排序" },
  { value: 2, label: "距离优先" }
];

interface StationListItem {
  distance: number;
  stationId: number;
  stationAddr: string;
  stationLabelList: {
    labelCode: string;
    labelName: string;
  }[];
  stationName: string;
}

export interface StationListRef {
  onGoodsListScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

interface StationListProps {
  requestParams?: any;
  requestApi?: any;
  autoFixedHeader?: boolean;
}

const StationList = forwardRef<StationListRef, StationListProps>(
  (props, ref) => {
    const { requestParams, autoFixedHeader = false } = props;
    // 用于跟踪外部调用次数的引用
    const externalCallCount = useRef(0);
    const [sortType, setSortType] = useState<1 | 2>(2);
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [isUseDefault, setIsUseDefault] = useState(false);
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [address, setAddress] = useState("");
    const [userLocation, setUserLocation] = useState<{
      lng: number;
      lat: number;
    } | null>(null);
    const couponIdFromUrl = getParamsFromHref("couponId") || "";

    // 暴露方法给父组件，并跟踪外部调用
    useImperativeHandle(ref, () => ({
      onGoodsListScroll: (e: React.UIEvent<HTMLDivElement>) => {
        // 增加外部调用计数
        externalCallCount.current += 1;
        // 调用实际的滚动处理逻辑
        onGoodsListScroll(e);
      }
    }));

    useEffect(() => {
      fetchLocation().catch(() => {});
    }, []);

    /**
     * 获取定位信息
     */
    const fetchLocation = async (isFirst = true) => {
      return new Promise(async (resolve, reject) => {
        try {
          setIsGettingLocation(true);
          const locationResult = await getUnifiedLocation();
          console.log("locationResult", locationResult);

          setUserLocation({ lng: locationResult.lng, lat: locationResult.lat });
          setAddress(locationResult.address || "");
          setIsUseDefault(!!locationResult.isUseDefault);

          if (locationResult.isUseDefault) {
            if (isFirst) {
              Toast.show({
                text: "当前定位服务未开启，当前定位到小鹏汽车总部",
                duration: 2000
              });
            } else {
              Toast.show({ text: "获取定位失败，请稍后重试", duration: 2000 });
            }
          }
          console.log(
            `定位成功，坐标: ${locationResult.lng}, ${locationResult.lat}, 是否使用默认位置: ${locationResult.isUseDefault}`
          );
          resolve(locationResult);
        } catch (error) {
          console.error("获取定位失败:", error);
          setIsUseDefault(false);
          reject(error);
        } finally {
          setIsGettingLocation(false);
        }
      });
    };

    // 处理排序切换
    const handleSortChange = (newSortType: 1 | 2) => {
      if (newSortType === sortType) {
        setShowFilterMenu(false);
        return;
      }

      setSortType(newSortType);
      setShowFilterMenu(false);

      // 重置分页和数据状态并重新加载数据
      mutate(initialData);
    };

    const initialData: StationListResponse = {
      list: [],
      pageNo: 0,
      hasMore: true
    };

    const {
      data,
      loading,
      loadingMore,
      noMore,
      loadMore,
      mutate,
      reload
    } = useInfiniteScroll<StationListResponse>(
      async (prevData?: StationListResponse): Promise<StationListResponse> => {
        const baseData: StationListResponse = prevData || initialData;

        if (!userLocation) return baseData;

        const nextPage = baseData.pageNo + 1;
        try {
          // 默认查询半径100km，每次5条；在请求时读取最新的 pageNo/sortType
          const params = {
            couponId: couponIdFromUrl,
            currentLat: userLocation.lat,
            currentLon: userLocation.lng,
            pageNo: nextPage,
            latitude: userLocation.lat,
            longitude: userLocation.lng,
            pageSize: 5,
            radius: 100000, // 单位米
            sortType,
            ...requestParams
          };

          const res: any = await findNearByStationV2(params);
          if (res.code !== 200) {
            return baseData;
          }

          // 前端过滤标签数量，至多展示5个
          const currentResult: StationListItem[] = (res.data?.result || []).map(
            (item: any) => {
              if (item?.stationLabelList?.length > 0) {
                item.stationLabelList = item.stationLabelList.filter(
                  (label: any, index: number) => index < 5
                );
              }
              return item as StationListItem;
            }
          );
          
          return {
            list: currentResult,
            pageNo: nextPage,
            hasMore: currentResult.length >= params.pageSize
          };
        } catch (error) {
          console.log("fetchStationList error -->", error);
          return baseData;
        }
      },
      {
        isNoMore: data => (data ? !data.hasMore : false),
        reloadDeps: [
          sortType,
          userLocation?.lat,
          userLocation?.lng,
          JSON.stringify(requestParams || {})
        ]
      }
    );
    const stationList = data?.list || [];
    const isLoadingAny = isGettingLocation || loading || loadingMore;
    const loadingText =
      stationList.length === 0 && sortType === 2
        ? "正在获取附近适用站点..."
        : "加载中...";
    const radiusMeters = Number(
      requestParams && typeof requestParams.radius !== "undefined"
        ? requestParams.radius
        : 100000
    );
    const radiusKm = Math.max(1, Math.round(radiusMeters / 1000));

    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const componentObserverRef = useRef<IntersectionObserver | null>(null);
    const headerObserverRef = useRef<IntersectionObserver | null>(null);
    const [isComponentVisible, setIsComponentVisible] = useState(true);
    const [isHeaderFixed, setIsHeaderFixed] = useState(false);
    // 设置滚动监听
    useEffect(() => {
      if (!autoFixedHeader) {
        return;
      }

      const setupObservers = () => {
        // 监听整个组件是否在视口中
        componentObserverRef.current = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              setIsComponentVisible(entry.isIntersecting);
              // 如果组件完全离开视口，隐藏固定头部
              if (!entry.isIntersecting) {
                setIsHeaderFixed(false);
              }
            });
          },
          {
            threshold: [0, 0.1], // 当组件刚进入或完全离开视口时触发
            rootMargin: "-10px 0px 0px 0px" // 稍微延迟触发
          }
        );

        // 监听头部是否到达视口顶部
        headerObserverRef.current = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              // 当头部元素离开视口顶部时，显示固定头部
              const top = isXPApp ? 50 : 0;

              if (
                !entry.isIntersecting &&
                entry.boundingClientRect.top < top &&
                isComponentVisible
              ) {
                setIsHeaderFixed(true);
              } else if (entry.isIntersecting) {
                setIsHeaderFixed(false);
              }
            });
          },
          {
            threshold: [0, 1],
            rootMargin: "0px 0px 0px 0px"
          }
        );

        // 开始观察
        if (containerRef.current) {
          componentObserverRef.current.observe(containerRef.current);
        }
        if (headerRef.current) {
          headerObserverRef.current.observe(headerRef.current);
        }
      };

      // 延迟设置观察器，确保DOM已经渲染
      const timer = setTimeout(setupObservers, 100);

      return () => {
        clearTimeout(timer);
        componentObserverRef.current?.disconnect();
        headerObserverRef.current?.disconnect();
      };
    }, []);

    const renderHeader = (isFixed?: boolean) => (
      <div className={`${isFixed ? "fixed-header" : ""}`}>
        {isUseDefault && (
          <div className="location-default-tip">
            <span className="desc">定位服务未开启，当前定位到小鹏汽车总部</span>
            <div
              className="btn"
              onClick={() =>
                fetchLocation(false)
                  .then(() => {
                    mutate(initialData);
                    reload();
                  })
                  .catch(() => {})
              }
            >
              重新开启
            </div>
          </div>
        )}
        <div className="dynamic-station-header">
          <div className="location-info">
            {(address || isGettingLocation) && (
              <>
                <img
                  className="icon"
                  src={`${CHARGE_CDN}/chargeVip/location.png`}
                  alt=""
                />
                <span className="address">
                  {isGettingLocation ? "正在获取位置信息..." : address}
                </span>
              </>
            )}
          </div>
          <div className="filter-section">
            <div
              className="filter-trigger"
              onClick={() => setShowFilterMenu(!showFilterMenu)}
            >
              <span className="filter-text">
                {sortOptions.find(opt => opt.value === sortType)?.label}
              </span>
              <img
                className={`filter-arrow ${showFilterMenu ? "active" : ""}`}
                src={`${CHARGE_CDN}/common/arrow_up.png`}
                alt=""
              />
            </div>
            {showFilterMenu && (
              <div className="filter-menu">
                {sortOptions.map(option => (
                  <div
                    key={option.value}
                    className={`filter-option ${
                      sortType === option.value ? "active" : ""
                    }`}
                    onClick={() => handleSortChange(option.value as 1 | 2)}
                  >
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );

    // 加载更多记录
    const loadMoreData = () => {
      if (loadingMore || loading || noMore || !userLocation) return;
      loadMore();
    };

    // 下拉到底部，翻页加载更多数据
    const onGoodsListScroll = e => {
      const isScrollToBottom =
        e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 1;
      if (isScrollToBottom) {
        if (!loadingMore && !loading && !noMore && stationList.length > 0) {
          loadMoreData();
        }
      }
    };

    return (
      <div className="station-list-content">
        <div className="station-list">
          <div className="title">适用站点</div>
          {/* 原始头部 */}
          <div ref={headerRef}>{renderHeader()}</div>

          {/* 固定头部 */}
          {isHeaderFixed && isComponentVisible && (
            <div className="fixed-header-container">{renderHeader(true)}</div>
          )}

          <div className="station-wrapper">
            {!!stationList.length &&
              stationList.map((item, index) => (
                <div className="station-content" key={index}>
                  <StationItem
                    station={item}
                    showDistance
                    showAddress
                    showAct
                    showTab
                  />
                </div>
              ))}
          </div>
        </div>
        {isLoadingAny ? (
          <div className="loading-more">
            <LoadingMore loadingText={loadingText} />
          </div>
        ) : (
          <div className="bottom-desc">
            {stationList.length > 0
              ? `以上为当前${radiusKm}公里范围内的适用站点`
              : `${radiusKm}公里内暂无当前适用站点`}
          </div>
        )}
      </div>
    );
  }
);

StationList.displayName = "StationList";

export default StationList;
